import React, { useState, useEffect } from 'react';
import { app } from './firebase';
import { getFirestore, collection, addDoc, deleteDoc, doc, setDoc, getDoc, onSnapshot } from 'firebase/firestore';

const db = getFirestore(app);

export default function MailingView({ vacancies, loading, logAction, isReadOnly }) {
  // --- STATE ---
  const [activeSection, setActiveSection] = useState('campaign'); 
  const [subscribers, setSubscribers] = useState([]);
  const [settings, setSettings] = useState({
    frequency: 'Weekly',
    autoMailing: true, 
    targetAudience: 'Consultants', // Default fixed to Consultants
    marketUpdateTitle: 'Market Update',
    marketUpdateBody: '',
    autoSelectVacancies: true,
    selectedVacancyIds: []
  });
  
  // Modals
  const [showSubModal, setShowSubModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [subForm, setSubForm] = useState({ email: '', name: '', type: 'Consultant', role: 'Infrastructure', subtype: 'Systems' });
  const [editingId, setEditingId] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [sending, setSending] = useState(false);

  const [campaignFilters, setCampaignFilters] = useState({
    targetRole: 'All',
    targetSubtype: 'All'
  });

  const ROLE_MAP = {
    'Infrastructure': ['Systems', 'Networking', 'Cloud', 'DevOps', 'Database'],
    'Development': ['Fullstack', 'Frontend', 'Backend', 'Mobile', 'Embedded', 'Data Engineering'],
    'Management': ['Project Management', 'Service Management', 'Product Owner/Scrum Master', 'IT Management/CTO'],
    'Security': ['Security Engineer', 'CISO/Compliance', 'Penetration Testing'],
    'Data & AI': ['Data Science', 'Business Intelligence', 'AI/Machine Learning'],
    'Support & Other': ['Support (L1/L2/L3)', 'Functional Analysis', 'Testing/QA', 'Other']
  };

  // --- LISTENERS ---
  useEffect(() => {
    const unsubSub = onSnapshot(collection(db, "subscribers"), (snap) => {
      setSubscribers(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    }, (error) => {
      console.error("Error loading subscribers:", error);
    });

    const fetchSettings = async () => {
      try {
        const docRef = doc(db, "settings", "mailing_config");
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const data = docSnap.data();
          // Force targetAudience to Consultants regardless of saved data
          setSettings({ ...data, targetAudience: 'Consultants' });
          if (data.filters) {
            setCampaignFilters(data.filters);
          }
        }
      } catch (error) {
        console.error("Error fetching settings:", error);
      }
    };
    fetchSettings();

    return () => unsubSub();
  }, []);

  // --- HANDLERS: SUBSCRIBERS ---
  const handleAddSubscriber = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...subForm,
        updatedAt: new Date().toISOString()
      };

      if (isEditing && editingId) {
        await setDoc(doc(db, "subscribers", editingId), data, { merge: true });
      } else {
        await addDoc(collection(db, "subscribers"), {
          ...data,
          joinedAt: new Date().toISOString(),
          status: 'Subscribed'
        });
      }
      
      setShowSubModal(false);
      setIsEditing(false);
      setEditingId(null);
      setSubForm({ email: '', name: '', type: 'Consultant', role: 'Infrastructure', subtype: 'Systems' });
    } catch (err) {
      console.error(err);
    }
  };

  const openEditModal = (sub) => {
    setSubForm({
      email: sub.email || '',
      name: sub.name || '',
      type: sub.type || 'Consultant',
      role: sub.role || 'Infrastructure',
      subtype: sub.subtype || 'Systems'
    });
    setEditingId(sub.id);
    setIsEditing(true);
    setShowSubModal(true);
  };

  const handleDeleteSubscriber = async (id) => {
    if(!window.confirm("Remove subscriber?")) return;
    await deleteDoc(doc(db, "subscribers", id));
  };

  // --- HANDLERS: CAMPAIGN ---
  const handleSettingChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  const toggleVacancySelection = (vacId) => {
    setSettings(prev => {
      const current = prev.selectedVacancyIds || [];
      if (current.includes(vacId)) {
        return { ...prev, selectedVacancyIds: current.filter(id => id !== vacId) };
      } else {
        return { ...prev, selectedVacancyIds: [...current, vacId] };
      }
    });
  };

  const saveSettings = async () => {
    if(isReadOnly) return alert("Permission Denied");
    try {
      // Ensure we save 'Consultants' as the audience
      await setDoc(doc(db, "settings", "mailing_config"), { 
        ...settings, 
        targetAudience: 'Consultants',
        filters: campaignFilters
      });
      logAction("Update Mailing Config", "Updated campaign settings");
      alert("Saved.");
    } catch (err) { console.error(err); }
  };

  const handleForceSend = async () => {
    if(isReadOnly) return alert("Permission Denied");
    if (!window.confirm("Are you sure you want to blast this email to ALL subscribers matching the selected targeting immediately?")) return;
    logAction("Force Send Campaign", "Manually triggered campaign");
    
    setSending(true);
    try {
      await addDoc(collection(db, "mail_logs"), {
        triggeredAt: new Date(),
        triggeredBy: 'Admin (Manual Force)',
        config: { 
          ...settings, 
          targetAudience: 'Consultants',
          filters: campaignFilters
        },
        status: 'Queued'
      });
      alert("Campaign queued for immediate delivery!");
    } catch (err) {
      console.error(err);
      alert("Failed to queue email.");
    } finally {
      setSending(false);
    }
  };

  const getPreviewVacancies = () => {
    const openVacancies = vacancies.filter(v => v.status === 'Open');
    if (settings.autoSelectVacancies) {
      return openVacancies.slice(0, 5);
    } else {
      return openVacancies.filter(v => settings.selectedVacancyIds?.includes(v.id));
    }
  };

  const openVacancies = vacancies.filter(v => v.status === 'Open');

  return (
    <div className="container-fluid p-0">
      {/* HEADER */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="fw-bold text-main">Mailing & Newsletter</h2>
          <p className="text-muted-theme">Manage subscribers and periodic vacancy updates.</p>
        </div>
        <div className="btn-group">
          <button className={`btn ${activeSection === 'campaign' ? 'btn-primary' : 'btn-outline-secondary'}`} onClick={() => setActiveSection('campaign')}>
            <i className="bi bi-gear-fill me-2"></i> Campaign Config
          </button>
          <button className={`btn ${activeSection === 'subscribers' ? 'btn-primary' : 'btn-outline-secondary'}`} onClick={() => setActiveSection('subscribers')}>
            <i className="bi bi-people-fill me-2"></i> Subscribers
          </button>
        </div>
      </div>

      {/* SECTION: CAMPAIGN CONFIGURATION */}
      {activeSection === 'campaign' && (
        <div className="row g-4">
          {/* LEFT: SETTINGS FORM */}
          <div className="col-lg-6">
            <div className="card h-100 bg-card border-theme shadow-sm">
              <div className="card-header border-theme d-flex justify-content-between align-items-center">
                <h5 className="mb-0 text-main">General Settings</h5>
                
                <div className="form-check form-switch">
                  <input 
                    className="form-check-input" 
                    type="checkbox" 
                    checked={settings.autoMailing || false}
                    onChange={(e) => handleSettingChange('autoMailing', e.target.checked)}
                  />
                  <label className="form-check-label small text-main">
                    {settings.autoMailing ? 'Auto-Sending ON' : 'Manual Only'}
                  </label>
                </div>
              </div>
              
              <div className="card-body">
                <div className="row g-3">
                  <div className="col-12">
                    <label className="form-label text-muted-theme small">Frequency</label>
                    <select className="form-select" value={settings.frequency || 'Weekly'} onChange={(e) => handleSettingChange('frequency', e.target.value)} disabled={!settings.autoMailing}>
                      <option>Weekly</option>
                      <option>Bi-Weekly</option>
                      <option>Monthly</option>
                    </select>
                  </div>
                  
                  {/* Target Audience selection removed - locked to Consultants */}
                  <div className="col-12">
                      <label className="form-label text-muted-theme small">Audience Targeting</label>
                      <div className="row g-2">
                          <div className="col-6">
                              <select 
                                  className="form-select form-select-sm" 
                                  value={campaignFilters.targetRole}
                                  onChange={e => setCampaignFilters({...campaignFilters, targetRole: e.target.value, targetSubtype: 'All'})}
                              >
                                  <option value="All">All Roles</option>
                                  {Object.keys(ROLE_MAP).map(r => <option key={r} value={r}>{r}</option>)}
                              </select>
                          </div>
                          <div className="col-6">
                              <select 
                                  className="form-select form-select-sm" 
                                  value={campaignFilters.targetSubtype}
                                  onChange={e => setCampaignFilters({...campaignFilters, targetSubtype: e.target.value})}
                                  disabled={campaignFilters.targetRole === 'All'}
                              >
                                  <option value="All">All Subtypes</option>
                                  {campaignFilters.targetRole !== 'All' && ROLE_MAP[campaignFilters.targetRole].map(s => <option key={s} value={s}>{s}</option>)}
                              </select>
                          </div>
                      </div>
                      <small className="text-muted-theme mt-1 d-block">Only subscribers matching this specialization will receive the campaign.</small>
                  </div>

                  <div className="col-12 mt-4">
                    <label className="form-label text-main fw-bold">Market Update Section</label>
                    <input className="form-control mb-2" placeholder="Title (e.g., Q3 Market Trends)" value={settings.marketUpdateTitle || ''} onChange={(e) => handleSettingChange('marketUpdateTitle', e.target.value)} />
                    <textarea className="form-control" rows="5" placeholder="Write your market update message here..." value={settings.marketUpdateBody || ''} onChange={(e) => handleSettingChange('marketUpdateBody', e.target.value)}></textarea>
                  </div>

                  {/* ACTION BUTTONS */}
                  <div className="col-12 mt-4 d-flex gap-2">
                    <button className="btn btn-outline-info w-50" onClick={() => setShowPreviewModal(true)}>Preview</button>
                    {!isReadOnly && (
                        <button className="btn btn-success w-50" onClick={saveSettings}>Save Config</button>
                    )}
                  </div>

                  {/* FORCE SEND AREA */}
                  {!isReadOnly && (
                      <div className="col-12 mt-4 pt-3 border-top border-theme">
                         <button className="btn btn-danger w-100" onClick={handleForceSend} disabled={sending}>
                            Force Send Campaign Now
                         </button>
                      </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT: JOB SELECTION */}
          <div className="col-lg-6">
            <div className="card h-100 bg-card border-theme shadow-sm">
              <div className="card-header border-theme d-flex justify-content-between align-items-center">
                <h5 className="mb-0 text-main">Job Selection</h5>
                <div className="form-check form-switch">
                  <input className="form-check-input" type="checkbox" checked={settings.autoSelectVacancies || false} onChange={(e) => handleSettingChange('autoSelectVacancies', e.target.checked)} />
                  <label className="form-check-label small text-muted-theme">Auto-select newest</label>
                </div>
              </div>
              
              <div className="card-body overflow-auto" style={{maxHeight: '600px'}}>
                {settings.autoSelectVacancies ? (
                  <div className="text-center py-5 text-muted-theme">
                    <i className="bi bi-robot fs-1 mb-3 d-block"></i>
                    <p>The system will automatically include the 5 most recently created open vacancies.</p>
                    <small>Turn off "Auto-select" to pick manually.</small>
                  </div>
                ) : (
                  <div className="list-group list-group-flush">
                    {openVacancies.length > 0 ? openVacancies.map(v => (
                      <label key={v.id} className="list-group-item bg-input border-theme d-flex align-items-center gap-3">
                        <input className="form-check-input flex-shrink-0" type="checkbox" checked={settings.selectedVacancyIds?.includes(v.id) || false} onChange={() => toggleVacancySelection(v.id)} />
                        <div className="flex-grow-1">
                          <div className="fw-bold text-main">{v.jobTitle}</div>
                          <small className="text-muted-theme">{v.customerName} | {v.region}</small>
                        </div>
                        <span className="badge bg-secondary">{v.contractType}</span>
                      </label>
                    )) : (
                      <p className="text-muted-theme text-center">No open vacancies available.</p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* SECTION: SUBSCRIBERS */}
      {activeSection === 'subscribers' && (
        <div className="card bg-card border-theme shadow-sm">
          <div className="card-header border-theme d-flex justify-content-between align-items-center p-3">
            <h5 className="mb-0 text-main">Subscriber List ({subscribers.length})</h5>
            <button className="btn btn-sm btn-primary" onClick={() => setShowSubModal(true)}><i className="bi bi-person-plus"></i> <span className="d-none d-sm-inline">Add User</span></button>
          </div>
          <div className="card-body p-0">
            <div className="table-responsive">
                <table className="table table-hover mb-0" style={{color: 'var(--text-main)', minWidth: '600px'}}>
                  <thead>
                    <tr>
                      <th className="border-bottom border-theme text-muted-theme bg-card ps-3">Name</th>
                      <th className="border-bottom border-theme text-muted-theme bg-card">Email</th>
                      <th className="border-bottom border-theme text-muted-theme bg-card">Specialization</th>
                      <th className="border-bottom border-theme text-muted-theme bg-card">Status</th>
                      <th className="border-bottom border-theme text-muted-theme bg-card text-end pe-3">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {subscribers.map(sub => (
                      <tr key={sub.id} className="border-theme">
                        <td className="bg-card text-main border-theme ps-3">{sub.name}</td>
                        <td className="bg-card text-main border-theme">{sub.email}</td>
                        <td className="bg-card border-theme">
                            <div className="small fw-bold text-main">{sub.role}</div>
                            <div className="small text-muted-theme">{sub.subtype}</div>
                        </td>
                        <td className="bg-card border-theme"><span className="badge bg-success">{sub.status}</span></td>
                        <td className="text-end bg-card border-theme pe-3">
                          <button className="btn btn-sm btn-link text-primary me-2" onClick={() => openEditModal(sub)}><i className="bi bi-pencil"></i></button>
                          <button className="btn btn-sm btn-link text-danger" onClick={() => handleDeleteSubscriber(sub.id)}><i className="bi bi-trash"></i></button>
                        </td>
                      </tr>
                    ))}
                    {subscribers.length === 0 && <tr><td colSpan="5" className="text-center text-muted-theme py-4 bg-card">No subscribers found.</td></tr>}
                  </tbody>
                </table>
            </div>
          </div>
        </div>
      )}

      {/* PREVIEW MODAL */}
      {showPreviewModal && (
        <div className="modal show d-block" style={{backgroundColor: 'rgba(0,0,0,0.8)'}}>
          <div className="modal-dialog modal-lg modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header border-theme">
                <h5 className="modal-title text-main">
                    <i className="bi bi-envelope-paper me-2"></i>
                    Email Preview
                </h5>
                <button className="btn-close" onClick={() => setShowPreviewModal(false)}></button>
              </div>
              <div className="modal-body p-4 bg-input">
                {/* EMAIL MOCKUP CONTAINER */}
                <div style={{fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif", backgroundColor: '#f4f7f6', padding: '20px'}}>
                    <div style={{maxWidth: '600px', margin: '0 auto', backgroundColor: '#ffffff', borderRadius: '10px', overflow: 'hidden', boxShadow: '0 4px 6px rgba(0,0,0,0.05)'}}>
                        
                        {/* Header */}
                        <div style={{backgroundColor: '#000000', color: '#ffffff', padding: '30px', textAlign: 'center'}}>
                            <div style={{fontSize: '28px', fontWeight: 'bold'}}>Nettic<span style={{color: '#00d2ff'}}>.</span></div>
                        </div>

                        {/* Content */}
                        <div style={{padding: '30px', color: '#333'}}>
                            <p style={{fontSize: '16px'}}>Hi [Subscriber Name],</p>
                            
                            {/* Market Update Section */}
                            <div style={{marginBottom: '30px'}}>
                                <h2 style={{color: '#1a202c', fontSize: '22px', marginBottom: '10px'}}>{settings.marketUpdateTitle || 'Market Update'}</h2>
                                <p style={{color: '#4a5568', lineHeight: '1.6', whiteSpace: 'pre-line'}}>
                                    {settings.marketUpdateBody || 'No market update content provided yet.'}
                                </p>
                            </div>

                            {/* Recommended Section (Mockup) */}
                            <div style={{borderBottom: '2px solid #0d6efd', paddingBottom: '10px', marginBottom: '20px', color: '#1a202c', fontSize: '20px', fontWeight: 'bold'}}>
                                Recommended for You
                            </div>
                            
                            {getPreviewVacancies().length > 0 ? (
                                getPreviewVacancies().map(v => (
                                    <div key={v.id} style={{marginBottom: '20px', padding: '20px', backgroundColor: '#ffffff', border: '1px solid #e1e4e8', borderRadius: '8px'}}>
                                        <h3 style={{margin: '0 0 10px 0', color: '#0d6efd', fontSize: '18px'}}>{v.jobTitle}</h3>
                                        <p style={{margin: '0 0 10px 0', color: '#4a5568', fontSize: '14px'}}>
                                            <strong>Region:</strong> {v.region} | <strong>Type:</strong> {v.contractType}
                                        </p>
                                        <p style={{margin: '0 0 15px 0', color: '#2d3748', fontSize: '15px', lineHeight: '1.5'}}>{v.shortDesc}</p>
                                        <a href="https://nettic.be/vacancies.html" style={{display: 'inline-block', padding: '10px 20px', backgroundColor: '#0d6efd', color: '#ffffff', textDecoration: 'none', borderRadius: '5px', fontWeight: 'bold', fontSize: '14px'}}>View Details</a>
                                    </div>
                                ))
                            ) : (
                                <p style={{color: '#718096', fontStyle: 'italic'}}>No vacancies selected for preview.</p>
                            )}

                            {/* Other Opportunities (Mockup) */}
                            <div style={{borderBottom: '2px solid #0d6efd', paddingBottom: '10px', margin: '40px 0 20px 0', color: '#1a202c', fontSize: '20px', fontWeight: 'bold'}}>
                                Other New Opportunities
                            </div>
                            <p style={{color: '#718096', fontSize: '14px', marginBottom: '15px'}}>Know someone who fits these roles? Feel free to share!</p>
                            <div style={{color: '#718096', fontStyle: 'italic', textAlign: 'center', padding: '20px', border: '1px dashed #cbd5e0', borderRadius: '8px'}}>
                                Other vacancies will be listed here...
                            </div>

                            <div style={{marginTop: '40px', textAlign: 'center'}}>
                                <p style={{color: '#4a5568'}}>Want to see more?</p>
                                <a href="https://nettic.be/vacancies.html" style={{color: '#0d6efd', fontWeight: 'bold', textDecoration: 'none'}}>Visit our full vacancies page &rarr;</a>
                            </div>
                        </div>

                        {/* Footer */}
                        <div style={{backgroundColor: '#f8fafc', color: '#718096', padding: '20px', textAlign: 'center', fontSize: '12px', borderTop: '1px solid #e1e4e8'}}>
                            <p>You are receiving this email because you are part of the Nettic consultant network.</p>
                            <p>
                                <a href="https://nettic.be/unsubscribe.html" style={{color: '#0d6efd', textDecoration: 'none'}}>Unsubscribe</a> | 
                                <a href="https://nettic.be" style={{color: '#0d6efd', textDecoration: 'none'}}>Visit Website</a>
                            </p>
                            <p style={{marginTop: '15px'}}>&copy; {new Date().getFullYear()} Nettic. All rights reserved.</p>
                        </div>
                    </div>
                </div>
              </div>
              <div className="modal-footer border-theme">
                <button className="btn btn-outline-secondary" onClick={() => setShowPreviewModal(false)}>Close Preview</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ADD SUBSCRIBER MODAL */}
      {showSubModal && (
        <div className="modal show d-block" style={{backgroundColor: 'rgba(0,0,0,0.8)'}}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header border-theme">
                <h5 className="modal-title text-main">{isEditing ? 'Edit Subscriber' : 'Add Subscriber'}</h5>
                <button className="btn-close" onClick={() => { setShowSubModal(false); setIsEditing(false); setEditingId(null); setSubForm({ email: '', name: '', type: 'Consultant', role: 'Infrastructure', subtype: 'Systems' }); }}></button>
              </div>
              <div className="modal-body">
                <form onSubmit={handleAddSubscriber}>
                  <div className="mb-3">
                    <label className="form-label text-muted-theme small">Full Name</label>
                    <input className="form-control" required value={subForm.name} onChange={e => setSubForm({...subForm, name: e.target.value})} />
                  </div>
                  <div className="mb-3">
                    <label className="form-label text-muted-theme small">Email Address</label>
                    <input type="email" className="form-control" required value={subForm.email} onChange={e => setSubForm({...subForm, email: e.target.value})} />
                  </div>
                  <div className="mb-3">
                    <label className="form-label text-muted-theme small">Type</label>
                    <select className="form-select" value={subForm.type} onChange={e => setSubForm({...subForm, type: e.target.value})}>
                        <option>Consultant</option>
                    </select>
                  </div>

                  <div className="row g-2 mb-3">
                    <div className="col-6">
                        <label className="form-label text-muted-theme small">Primary Role</label>
                        <select 
                            className="form-select" 
                            value={subForm.role} 
                            onChange={e => setSubForm({...subForm, role: e.target.value, subtype: ROLE_MAP[e.target.value][0]})}
                        >
                            {Object.keys(ROLE_MAP).map(r => <option key={r} value={r}>{r}</option>)}
                        </select>
                    </div>
                    <div className="col-6">
                        <label className="form-label text-muted-theme small">Subtype</label>
                        <select 
                            className="form-select" 
                            value={subForm.subtype} 
                            onChange={e => setSubForm({...subForm, subtype: e.target.value})}
                        >
                            {ROLE_MAP[subForm.role].map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                    </div>
                  </div>

                  <button type="submit" className="btn btn-primary w-100">{isEditing ? 'Save Changes' : 'Add to List'}</button>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}